import { useState, useEffect, useRef } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useAuth0 } from '@auth0/auth0-react';
import { apiCallErrorHandler } from '../utils/common';
import './QueueForm.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const sourceTitle = (i) => (process.env.REACT_APP_HIDE_SOURCES === '1'
  ? `Source ${i}`
  : ['DAT', 'Outvin', 'Vincario', 'ChatGPT', 'Claude'][i - 1]);
const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/i;
// A value is worth translating only if it contains an actual word (2+ letters),
// not a pure code/number/VIN.
const WORD_REGEX = /\p{L}{2,}/u;
const containsWords = (v) => typeof v === 'string' && WORD_REGEX.test(v);
// These fields are proper nouns / identifiers — never translate them.
const NO_TRANSLATE_FIELDS = new Set(['VIN', 'Make', 'Model', 'SubModel', 'Submodel', 'Version', 'Equipments info']);
// Fields whose consolidated values feed the registration-tax calculation.
const CO2_FIELD = 'CO2';
const WEIGHT_FIELD = 'Weight Empty (kg)';
// Tax endpoint expects ISO 3166-1 alpha-2; our market picker uses 'UK' for the United Kingdom.
const toTaxCountry = (m) => (m === 'UK' ? 'GB' : m);
// Keep only the digits of a value like "120 g/km" or "1,650 kg".
const toInt = (v) => {
  const n = parseInt(String(v ?? '').replace(/[^\d]/g, ''), 10);
  return Number.isFinite(n) ? n : null;
};

const MARKETS = [
  { value: 'DE', flag: '🇩🇪' },
  { value: 'FR', flag: '🇫🇷' },
  { value: 'ES', flag: '🇪🇸' },
  { value: 'IT', flag: '🇮🇹' },
  { value: 'PT', flag: '🇵🇹' },
  { value: 'NL', flag: '🇳🇱' },
  { value: 'BE', flag: '🇧🇪' },
  { value: 'PL', flag: '🇵🇱' },
  { value: 'UK', flag: '🇬🇧' },
];

const authHeader = (token) => (token ? { Authorization: `Bearer ${token}` } : {});

// Lite is public: in production its calls go through the UI app's reverse proxy (see public/web.config),
// which adds the BE shared secret server-side.
const LITE_API_BASE = '/lite-api';

export default function VinDecoderForm({ lite = false }) {
  const apiBase = lite && process.env.NODE_ENV === 'production' ? LITE_API_BASE : API_BASE_URL;
  const toast = useRef(null);
  const vinRef = useRef(null);
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  useEffect(() => { document.title = 'OptiConfig VIN Decoder'; }, []);
  useEffect(() => { vinRef.current?.focus(); }, []);

  const [vin, setVin]         = useState('');
  const [market, setMarket]   = useState('DE');
  const [loading, setLoading] = useState(false);
  const [source1Result, setSource1Result] = useState(null); // { data, loading, error }
  const [source2Result, setSource2Result] = useState(null);
  const [source3Result, setSource3Result] = useState(null);
  const [source4Result, setSource4Result] = useState(null);
  const [source5Result, setSource5Result] = useState(null);
  const [consolidatedFields, setConsolidatedFields] = useState({}); // { fieldName: { value, confidencePrc } }
  const sourceDataRef = useRef({}); // { sourceEnum: data }
  const tokenRef = useRef(null);
  const taxRequestedRef = useRef(false);   // guard: fire the tax call once per decode
  const vehicleDescRef = useRef('');        // same description string passed to consolidate
  const marketRef = useRef('DE');
  const [sessionId]           = useState(() => crypto.randomUUID());
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sourcesExpanded, setSourcesExpanded] = useState(false);

  const toggleRow = (key) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const vinValid = VIN_REGEX.test(vin.trim());
  const vinError = vin.length > 0 && !vinValid;

  const showToast = (severity, summary, detail) => {
    toast.current?.show({ severity, summary, detail, life: 4000 });
  };

  const showTaxToast = (tax, country) => {
    const severity = !tax.hasMalus ? 'success'
      : (tax.severity === 'very_high' || tax.severity === 'high') ? 'error'
      : tax.severity === 'medium' ? 'warn' : 'info';
    const amount = `${Number(tax.amount).toLocaleString()} ${tax.currency || '€'}`;
    const detail = [tax.taxName, tax.notes].filter(Boolean).join(' — ');
    toast.current?.show({
      severity,
      summary: `${country} registration tax: ${amount}`,
      detail,
      sticky: true,
    });
  };

  const requestTax = async (emission, mass) => {
    if (lite) return; // never request a tax estimate in lite mode
    const country = toTaxCountry(marketRef.current);
    const params = new URLSearchParams({
      country,
      emission: String(emission),
      mass: String(mass),
      vehicleDescription: vehicleDescRef.current,
    });
    try {
      const res = await fetch(`${API_BASE_URL}/tax/calculate?${params}`, {
        headers: { ...authHeader(tokenRef.current) },
      });
      if (!res.ok) return;
      showTaxToast(await res.json(), country);
    } catch { /* tax estimate is best-effort */ }
  };

  // Once both CO2 and curb weight are consolidated, fire the registration-tax estimate (once per decode).
  useEffect(() => {
    if (lite || taxRequestedRef.current) return; // no tax estimate in lite mode
    const emission = toInt(consolidatedFields[CO2_FIELD]?.value);
    const mass = toInt(consolidatedFields[WEIGHT_FIELD]?.value);
    if (emission === null || mass === null) return;
    taxRequestedRef.current = true;
    requestTax(emission, mass);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consolidatedFields]);

  const handleDecode = async () => {
    if (!vinValid) return;
    setLoading(true);
    if (!lite) setSource1Result({ data: null, loading: true, error: null });
    setSource2Result({ data: null, loading: true, error: null });
    setSource3Result({ data: null, loading: true, error: null });
    setSource4Result(null);
    setSource5Result(null);
    setConsolidatedFields({});
    sourceDataRef.current = {};
    taxRequestedRef.current = false;
    toast.current?.clear();

    // Lite mode is public: fall back to anonymous calls when not signed in.
    let token = null;
    if (!lite || isAuthenticated) {
      try { token = await getAccessTokenSilently(); } catch (e) { if (!lite) throw e; }
    }
    tokenRef.current = token;

    const consolidateField = (field) => {
      if (field === 'Equipments info') return;
      const allSources = sourceDataRef.current;
      const vals = Object.entries(allSources)
        .map(([src, data]) => ({ source: src, value: data?.[field] }))
        .filter(({ value }) => value !== null && value !== undefined && value !== '');
      if (vals.length === 0) return;
      if (vals.length === 1) {
        const value = vals[0].value;
        // No consolidation needed — translate in parallel if it contains words.
        const willTranslate = !NO_TRANSLATE_FIELDS.has(field) && containsWords(value);
        setConsolidatedFields(prev => ({ ...prev, [field]: { value, confidencePrc: 100, singleSource: true, pendingTranslation: willTranslate } }));
        return willTranslate ? { field, value: String(value) } : undefined;
      }
      const distinct = [...new Set(vals.map(v => v.value))];
      if (distinct.length <= 1) {
        const value = distinct[0] || '';
        // No consolidation needed — translate in parallel if it contains words.
        const willTranslate = !NO_TRANSLATE_FIELDS.has(field) && containsWords(value);
        setConsolidatedFields(prev => ({ ...prev, [field]: { value, confidencePrc: 100, pendingTranslation: willTranslate } }));
        return willTranslate ? { field, value: String(value) } : undefined;
      }
      const sourceMap = {};
      for (const { source, value } of vals) sourceMap[source] = value;
      fetch(`${apiBase}/file/consolidate/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader(tokenRef.current) },
        body: JSON.stringify({
          fieldName: field,
          sourceValues: sourceMap,
          vehicleDescription: [baseRow.make, baseRow.model, baseRow.submodel, baseRow.version].filter(Boolean).join(' '),
        }),
      })
        .then(r => r.ok ? r.json() : null)
        .then(result => {
          if (result) setConsolidatedFields(prev => ({ ...prev, [field]: { value: result.value, valueEn: result.valueEn || null, confidencePrc: result.confidencePrc } }));
        })
        .catch(() => {});
    };

    // Batch-translate values that didn't need consolidation but contain words,
    // filling ValueEn in the result table.
    const translateNonConsolidated = (items) => {
      if (items.length === 0) return;
      const uniqueValues = [...new Set(items.map(i => i.value))];
      // Always clear the pending flag (even on failure) so spinners stop.
      const finish = (translations) => {
        setConsolidatedFields(prev => {
          const next = { ...prev };
          for (const { field, value } of items) {
            if (!next[field]) continue;
            const valueEn = translations?.[value];
            next[field] = { ...next[field], pendingTranslation: false, ...(valueEn ? { valueEn } : {}) };
          }
          return next;
        });
      };
      fetch(`${apiBase}/chat/translate/English`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader(tokenRef.current) },
        body: JSON.stringify(uniqueValues),
      })
        .then(r => r.ok ? r.json() : null)
        .then(finish)
        .catch(() => finish(null));
    };

    const triggerConsolidation = (fieldKeys) => {
      const toTranslate = [];
      for (const field of fieldKeys) {
        const pending = consolidateField(field);
        if (pending) toTranslate.push(pending);
      }
      translateNonConsolidated(toTranslate);
    };

    const sePromises = [];

    const fetchSource = async (source, setResult, extraRow) => {
      try {
        const row = { vin: vin.trim().toUpperCase(), ...(extraRow || {}) };
        const res = await fetch(`${apiBase}/file/enrich-single/${sessionId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader(token) },
          body: JSON.stringify({
            row,
            market,
            language: 'EN',
            source,
          }),
        });
        if (!res.ok) await apiCallErrorHandler(res, `Decode failed (${source})`);
        const data = await res.json();
        sourceDataRef.current[source] = data;
        setResult({ data, loading: false, error: null });

        // Queue simplified-equipment request (resolved later before consolidation)
        const equipInfo = data?.['Equipments info'];
        if (equipInfo) {
          const sePromise = fetch(`${apiBase}/file/simplified-equipment/${sessionId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeader(token) },
            body: JSON.stringify(equipInfo),
          })
            .then(r => r.ok ? r.json() : null)
            .then(seData => {
              if (seData) {
                sourceDataRef.current[source] = { ...sourceDataRef.current[source], ...seData };
                setResult(prev => prev?.data ? { ...prev, data: { ...prev.data, ...seData } } : prev);
              }
            })
            .catch(() => {});
          sePromises.push(sePromise);
        }

        return data;
      } catch (err) {
        console.error(`Source ${source} failed:`, err.message);
        setResult({ data: null, loading: false, error: null });
        return null;
      }
    };

    // Phase 1: sources 1-3 in parallel (lite: only Outvin + Vincario)
    const [source1Data, source2Data, source3Data] = lite
      ? [null, ...await Promise.all([
          fetchSource(2, setSource2Result),
          fetchSource(3, setSource3Result),
        ])]
      : await Promise.all([
          fetchSource(1, setSource1Result),
          fetchSource(2, setSource2Result),
          fetchSource(3, setSource3Result),
        ]);

    // Coalesce VIN/Make/Model/Submodel/Version from phase 1 results
    const coalesce = (field) => {
      for (const d of [source1Data, source2Data, source3Data]) {
        const v = d?.[field];
        if (v !== null && v !== undefined && v !== '') return v;
      }
      return '';
    };

    const baseRow = {
      make: coalesce('Make'),
      model: coalesce('Model'),
      submodel: coalesce('SubModel') || coalesce('Submodel'),
      version: coalesce('Version'),
    };

    // Capture the description/market the tax effect will use (same string consolidate sends).
    vehicleDescRef.current = [baseRow.make, baseRow.model, baseRow.submodel, baseRow.version].filter(Boolean).join(' ');
    marketRef.current = market;

    // Phase 2: sources 4-5 in parallel with enriched row data
    setSource4Result({ data: null, loading: true, error: null });
    if (!lite) setSource5Result({ data: null, loading: true, error: null });

    await Promise.all([
      fetchSource(6, setSource4Result, baseRow),
      ...(lite ? [] : [fetchSource(15, setSource5Result, baseRow)]),
    ]);

    // Phase 3: Wait for all simplified-equipment requests, then consolidate
    await Promise.all(sePromises);
    const allFieldKeys = new Set();
    for (const data of Object.values(sourceDataRef.current)) {
      if (data) Object.keys(data).forEach(k => allFieldKeys.add(k));
    }
    triggerConsolidation(allFieldKeys);

    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && vinValid && !loading) handleDecode();
  };

  const CombinedTable = ({ results }) => {
    // results: [{ title, result }]
    const sources = results.filter(r => r.result);
    if (sources.length === 0) return null;

    const anyLoading = sources.some(r => r.result.loading);
    const allDone = sources.every(r => !r.result.loading);

    // Collect all field keys in order (preserving first-seen order across sources)
    const allKeys = [];
    const seen = new Set();
    for (const { result } of sources) {
      if (!result.data) continue;
      for (const key of Object.keys(result.data)) {
        if (!seen.has(key)) { seen.add(key); allKeys.push(key); }
      }
    }

    const entries = allKeys.filter(key =>
      sources.some(({ result }) => {
        const v = result.data?.[key];
        return v !== '' && v !== null && v !== undefined;
      })
    ).sort((a, b) => (a === 'Equipments info') - (b === 'Equipments info'));

    return (
      <div>
        {anyLoading && (
          <div style={{ padding: '0.5rem', textAlign: 'center' }}>
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.2rem' }} />
            {' '}Loading…
          </div>
        )}
        {allDone && entries.length === 0 && (
          <div style={{ color: '#888', fontSize: '0.8rem' }}>No data returned.</div>
        )}
        {entries.length > 0 && (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem', tableLayout: 'fixed' }}>
              <thead>
                <tr style={{ background: '#f4f4f4' }}>
                  <th style={{ padding: '5px 10px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #ddd', width: sourcesExpanded ? '35%' : undefined }}>Field</th>
                  <th style={{ padding: '5px 10px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #ddd', width: '1.8rem' }}></th>
                  <th style={{ padding: '5px 10px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #ddd', background: '#e8f5e9' }}>
                    Result
                    <span
                      onClick={() => setSourcesExpanded(p => !p)}
                      style={{ marginLeft: '0.5rem', cursor: 'pointer', fontSize: '0.65rem', color: '#888', userSelect: 'none' }}
                      title={sourcesExpanded ? 'Hide sources' : 'Show sources'}
                    >
                      <i className={`pi ${sourcesExpanded ? 'pi-chevron-left' : 'pi-chevron-right'}`} style={{ fontSize: '0.6rem' }} />
                    </span>
                  </th>
                  <th style={{ padding: '5px 10px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #ddd', background: '#e8f5e9' }}>Result EN</th>
                  {sourcesExpanded && sources.map(s => (
                    <th key={s.title} style={{ padding: '5px 10px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #ddd' }}>{s.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map((key, i) => {
                  const values = sources.map(s => s.result.data?.[key]);
                  const nonEmpty = values.map(v => v !== null && v !== undefined && v !== '' ? String(v) : '');
                  const defaultResult = nonEmpty.find(v => v !== '') ?? '';
                  const allSame = nonEmpty.every(v => v === nonEmpty[0]);
                  const consolidated = consolidatedFields[key];
                  const resultVal = consolidated ? consolidated.value : defaultResult;
                  const confidenceColor = consolidated
                    ? consolidated.confidencePrc >= 80 ? '#2e7d32'
                      : consolidated.confidencePrc >= 50 ? '#f9a825'
                      : '#c62828'
                    : undefined;
                  const prefixColor = (v) => v.startsWith('✓') ? '#2e7d32' : v.startsWith('✗') ? '#cc0000' : v.startsWith('?') ? '#e65100' : undefined;
                  const isCollapsible = key === 'Equipments info';
                  const isExpanded = expandedRows.has(key);
                  const cellStyle = isCollapsible && !isExpanded
                    ? { maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                    : isCollapsible
                    ? { whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word' }
                    : {};
                  return (
                    <tr key={key} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa', borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '4px 10px', fontWeight: 500, color: '#555', verticalAlign: 'top' }}>
                        {isCollapsible ? (
                          <span
                            onClick={() => toggleRow(key)}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            title={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            <i className={`pi ${isExpanded ? 'pi-chevron-down' : 'pi-chevron-right'}`}
                               style={{ fontSize: '0.7rem', marginRight: '0.3rem' }} />
                            {key}
                          </span>
                        ) : key}
                      </td>
                      <td style={{ padding: '2px 0', textAlign: 'center', verticalAlign: 'top' }}>
                        {consolidated?.pendingTranslation ? (
                          <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.6rem', color: '#aaa' }} />
                        ) : consolidated && !consolidated.singleSource ? (
                          <span
                            title={`Confidence: ${consolidated.confidencePrc}%`}
                            style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              width: '1rem', height: '1rem', borderRadius: '50%',
                              background: confidenceColor, color: '#fff',
                              fontSize: '0.55rem', fontWeight: 700, lineHeight: 1,
                            }}
                          >
                            {consolidated.confidencePrc >= 80 ? '✓' : consolidated.confidencePrc >= 50 ? '?' : '!'}
                          </span>
                        ) : consolidated?.singleSource ? (
                          <span
                            title="Only 1 source"
                            style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              width: '1rem', height: '1rem', borderRadius: '50%',
                              background: '#bbb', color: '#fff',
                              fontSize: '0.55rem', fontWeight: 700, lineHeight: 1,
                            }}
                          >
                            1
                          </span>
                        ) : key !== 'Equipments info' && defaultResult !== '' && !consolidated ? (
                          <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.6rem', color: '#aaa' }} />
                        ) : null}
                      </td>
                      <td style={{
                        padding: '4px 10px',
                        fontWeight: 600,
                        background: '#f1f8e9',
                        fontFamily: resultVal.length === 17 ? 'monospace' : 'inherit',
                        wordBreak: 'break-word',
                        verticalAlign: 'top',
                        color: prefixColor(resultVal),
                        ...cellStyle,
                      }}>
                        {resultVal}
                      </td>
                      <td style={{
                        padding: '4px 10px',
                        fontWeight: 600,
                        background: '#f1f8e9',
                        wordBreak: 'break-word',
                        verticalAlign: 'top',
                        color: consolidated?.valueEn ? '#1565c0' : '#999',
                        fontStyle: consolidated?.valueEn ? 'normal' : 'italic',
                        ...cellStyle,
                      }}>
                        {consolidated?.valueEn || ''}
                      </td>
                      {sourcesExpanded && nonEmpty.map((str, vi) => {
                        const isDiff = !allSame && str !== '';
                        return (
                          <td key={vi} style={{
                            padding: '4px 10px',
                            fontFamily: str.length === 17 ? 'monospace' : 'inherit',
                            wordBreak: 'break-word',
                            background: isDiff ? '#fff8e1' : undefined,
                            verticalAlign: 'top',
                            color: prefixColor(str),
                            ...cellStyle,
                          }}>
                            {str}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ marginTop: '0.35rem', fontSize: '0.72rem', color: '#aaa' }}>{entries.length} fields</div>
          </>
        )}
      </div>
    );
  };

  const inner = (
    <div className="queue-form card" style={{ maxWidth: sourcesExpanded ? '100%' : 900, margin: '0 auto' }}>
      <Toast ref={toast} />
      {/* <h3 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>VIN Decoder</h3> */}

      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1.25rem', justifyContent: 'center' }}>
        {/* column: VIN input above flags row — column width is driven by the flags row */}
        <div style={{ display: 'inline-flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 600 }}>VIN</label>
            <InputText
              ref={vinRef}
              value={vin}
              onChange={(e) => setVin(e.target.value.trim())}
              onKeyDown={handleKeyDown}
              placeholder="17-character VIN"
              maxLength={17}
              style={{ background: lite ? '#f6f9f2' : 'none', fontFamily: 'monospace', letterSpacing: '0.05em', textTransform: 'uppercase', width: '100%', boxSizing: 'border-box' }}
              className={vinError ? 'p-invalid' : ''}
            />
            {vinError && (
              <small style={{ color: 'red' }}>VIN must be exactly 17 characters</small>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {MARKETS.map(({ value, flag }) => (
            <button
              key={value}
              onClick={() => setMarket(value)}
              title={value}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '3px 8px',
                border: market === value ? '2px solid #2196f3' : '2px solid transparent',
                borderRadius: 6,
                background: market === value ? '#e3f2fd' : '#f4f4f4',
                cursor: 'pointer',
                fontSize: '1.1rem',
                fontWeight: market === value ? 700 : 400,
                color: '#333',
                transition: 'border 0.15s, background 0.15s',
              }}
            >
              <span>{flag}</span>
              <span style={{ fontSize: '0.7rem' }}>{value}</span>
            </button>
          ))}
          </div>
        </div>

        <Button
          label="Search"
          icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-search'}
          disabled={!vinValid || loading}
          onClick={handleDecode}
        />
      </div>

      {(source1Result || source2Result || source3Result || source4Result || source5Result) && (
        <CombinedTable results={[
          // Lite hides real source names and numbers only the sources it actually runs.
          { title: sourceTitle(1), result: source1Result },
          { title: lite ? 'Source 1' : sourceTitle(2), result: source2Result },
          { title: lite ? 'Source 2' : sourceTitle(3), result: source3Result },
          { title: lite ? 'Source 3' : sourceTitle(4), result: source4Result },
          { title: sourceTitle(5), result: source5Result },
        ]} />
      )}
    </div>
  );

  return inner;
}
